import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';

// Import Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

// Import Components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Loading from './components/Loading/Loading';

// Import Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import SkillsPage from './pages/SkillsPage';
import ProjectsPage from './pages/ProjectsPage';
import ContactPage from './pages/ContactPage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';

// Import Context
import { ThemeProvider } from './context/ThemeContext';

// Import custom CSS
import './App.css';
import './dark-mode-contrast.css';
import './master-responsive.css';
import './mobile-touch.css';
import './theme.css';
import './cross-browser-compatibility.css';
// Import responsive styles
import './components/responsive.css';
import './components/Skills/skills-responsive.css';
import './components/Projects/projects-responsive.css';
import './components/Projects/projects-sections-responsive.css';
import './components/ProjectSlider/projectslider-responsive.css';
import './components/Footer/footer-responsive.css';
import './components/Hero/hero-responsive.css';
import './components/Contact/contact-responsive.css';
import './components/About/about-responsive.css';

// Import cross-browser compatibility utilities
import initCrossBrowserCompatibility from './utils/crossBrowserUtils';

// ScrollToTop Component
const ScrollToTop: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <div className="scroll-to-top">
      {isVisible && (
        <motion.button
          onClick={scrollToTop}
          className="scroll-btn"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          ↑
        </motion.button>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    // Initialize cross-browser compatibility utilities
    initCrossBrowserCompatibility();
    
    // Simulate loading time for assets
    const timer = setTimeout(() => {
      document.body.style.overflow = isLoading ? 'hidden' : 'visible';
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isLoading]);

  return (
    <ThemeProvider>
      <Router>
        <div className="app">
          {/* Loading screen */}
          <Loading isLoading={isLoading} setIsLoading={setIsLoading} />
          
          {/* Progress bar */}
          <motion.div className="progress-bar" style={{ scaleX }} />
          
          {/* Header */}
          <Header />
          
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/project/:id" element={<ProjectDetailsPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Routes>
          </main>
          
          <Footer />
          
          {/* Scroll To Top Button */}
          <ScrollToTop />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
